/**
 * GENERATED from source/representative-learning-design.md, verbatim. Do not edit the words here: edit the doc
 * (Coleman's) and regenerate, or the verbatim test fails. Structure (block
 * kinds, chapters, exercises, and each block's depth) is decided in
 * scripts/build-concepts.py; depth.js renders the Platform and
 * Certification versions from this one master lesson. See CLAUDE.md,
 * "Concept modules are verbatim", and .claude/skills/concept-module/SKILL.md.
 */

export const REPRESENTATIVE_LEARNING_DESIGN = {
  "key": "representative-learning-design",
  "title": "Representative Learning Design",
  "category": "Concepts",
  "platform": {
    "key": "representative-learning-design",
    "sub": "How Much of the Game Do We Really Need?",
    "duration": "8–10 min read",
    "chapter": {
      "id": "representative-learning-design",
      "title": "Representative Learning Design"
    }
  },
  "cert": {
    "key": "representative-learning-design-cert",
    "duration": "20–25 min"
  },
  "chapters": [
    {
      "id": "drill",
      "title": "A Drill Can Look Like Basketball and Still Miss the Point",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "A Drill Can Look Like Basketball and Still Miss the Point"
        },
        {
          "kind": "text",
          "md": "Think about a workout that has defenders, cones marking spots, players moving at full speed, and someone yelling that it is “game-like.” It certainly looks like basketball.\n\nBut what is the athlete actually practicing? What information guides their action? What are they trying to accomplish? And does the task create enough opportunities to work on the quality we care about?\n\nThose questions lead us to **Representative Learning Design (RLD)**: designing practice so that the demands that matter in competition are present when athletes learn to respond to them.\n\nThe idea is not that every workout has to recreate a full five-on-five game. It is that the relationship between the athlete and the game matters. An athlete does not just execute movements. They act in response to space, teammates, defenders, timing, intentions, and changing opportunities."
        },
        {
          "kind": "bigIdea",
          "md": "You don't need to recreate the entire game. You need to recreate the parts of the game that matter for the problem you're training."
        },
        {
          "kind": "text",
          "md": "This is also why a drill can **look** realistic while missing an important piece of reality. Defenders might be present but behave nothing like the defender the athlete needs to read. The athlete might run the right route but already know exactly where to pass. Visual resemblance is a clue, not proof that the task represents the target situation."
        }
      ]
    },
    {
      "id": "what",
      "title": "Representative of What?",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "Representative of What?"
        },
        {
          "kind": "text",
          "md": "Instead of treating practice as either representative or not representative, consider the different parts of competition it may reproduce."
        },
        {
          "kind": "table",
          "head": [
            "Dimension",
            "What it might mean in practice"
          ],
          "rows": [
            {
              "cells": [
                "**Space**",
                "Court geometry, spacing, angles, and distances."
              ]
            },
            {
              "cells": [
                "**Time**",
                "Playing speed, available decision windows, and time to organize a movement."
              ]
            },
            {
              "cells": [
                "**Information**",
                "Defender movement, help, teammate positioning, and other cues the athlete must perceive."
              ]
            },
            {
              "cells": [
                "**Movement and options**",
                "The range of footwork, releases, finishes, or other action solutions that are available."
              ]
            },
            {
              "cells": [
                "**Tactics and people**",
                "Real decisions involving teammates, defenders, roles, and changing numerical relationships."
              ]
            },
            {
              "cells": [
                "**Physical demands**",
                "Contact, force, pace, fatigue, and repeated efforts."
              ]
            },
            {
              "cells": [
                "**Psychological demands**",
                "Competition, stakes, excitement, hesitation, and pressure."
              ]
            },
            {
              "cells": [
                "**Task goal**",
                "Whether athletes are pursuing a game-like outcome or a deliberately different training objective."
              ]
            },
            {
              "cells": [
                "**Game flow**",
                "Continuous play, including the shifts between offense and defense, rather than endless isolated restarts."
              ]
            }
          ]
        },
        {
          "kind": "text",
          "md": "These are **BAM's practical design lenses**, not nine officially established scientific categories. They overlap, and you do not need to match all nine in every activity.\n\nIf the goal is an off-ball read, the informational and tactical pieces may be essential. If the goal is to develop a particular coordination pattern, matching every teammate's position may add complexity without helping that pattern improve.\n\nThe question is not, “How much of the game can I cram into this drill?” It is, **“What does the athlete need from the game to learn this particular thing?”**"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Overall vs. Target-Specific Representativeness"
        },
        {
          "kind": "text",
          "md": "**Overall representativeness** asks how much of the complete game environment the activity reproduces.\n\n**Target-specific representativeness** asks whether it preserves the demands that are most important for the quality you are developing.\n\nBoth matter. But target-specific representativeness is usually the more useful question when building a workout.\n\nTake the BAM **2v2 half-advantage finishing-versus-dump-off situation**. The ball handler starts with a defender on their hip, with help and a teammate available. It is clearly not a complete five-on-five possession. We have removed much of the court, most of the players, and lots of tactical possibilities.\n\nBut for the specific question, **“Do I finish or play the ball to my teammate when help arrives?”**, the athlete still encounters live information: a defender recovering, help changing the picture, and a teammate whose availability matters.\n\nThat can be more target-specific than a busy-looking five-on-five activity in which the player rarely encounters that situation.\n\n**BAM Insight:** A small, well-designed slice of basketball can be highly representative of a particular problem without being representative of everything that happens in basketball."
        }
      ]
    },
    {
      "id": "deepdive",
      "title": "Certification Deep Dive | Designing for Transfer",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "Certification Deep Dive | Designing for Transfer",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Representativeness has multiple dimensions. A well-designed activity preserves the game demands that matter for the skill or decision we're developing, rather than trying to recreate every detail of a full game.\n\nNow we can ask the tougher questions: What counts as relevant information? How do we know a task actually samples it? When does simplifying a task preserve its function, and when does it accidentally change the skill we are training?",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "1. Where Representative Learning Design Comes From",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "The scientific term **Representative Learning Design (RLD)** grew from research on representative experimental design and ecological approaches to perception and action. Pinder, Davids, Renshaw, and Araújo (2011) brought the idea into sport practice and research, emphasizing **functionality** and **action fidelity**.\n\nIn coach language:\n\n- **Functionality:** Does the athlete interact with the information that normally regulates the target action in competition?\n- **Action fidelity:** Does that interaction invite movement and action solutions that correspond to what the athlete would need in the game?\n\nThat does **not** mean every drill must include ten players and two referees. It means we should take the actual performance problem seriously.\n\nThis is a **theoretical design framework**, not proof that every RLD-inspired drill produces superior learning. The amount and type of fidelity that is sufficient for a particular athlete, skill, or stage of learning remains an empirical and coaching question.",
          "depth": "cert"
        },
        {
          "kind": "scienceNerd",
          "title": "Representative design isn't just “ecological validity”",
          "blocks": [
            {
              "kind": "prose",
              "md": "Pinder et al. (2011) revisited Egon Brunswik's distinction between representative design and ecological validity. Their proposal was to sample the constraints and information-action relationships people encounter in performance, rather than judge a training or laboratory task merely by its appearance or location. They connected that logic to nonlinear pedagogy and the constraints-led approach. This is conceptual groundwork, not an intervention trial proving a specific practice recipe. [Study: https://doi.org/10.1123/jsep.33.1.146]"
            }
          ],
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "2. What Counts as the Information That Matters?",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "For decision-making targets, a useful way to begin is to work backward from the moment an action becomes possible.\n\nUse the BAM half-advantage 2v2. The target is not simply “make the dump-off pass.” The target is recognizing when a finish remains available and when the help defender's response opens a pass.\n\nIf the defender is absent, the ball handler can practice the pass technique but not necessarily the read. If the defender is present but is required to stand still, the player may face a different decision. If help changes behavior unpredictably while the teammate becomes available at realistic times, the athlete has a more relevant problem to solve.\n\nNotice how much of the full game we have still removed. We are not trying to reproduce every crowd noise, every weak-side rotation, and the prior 18 seconds of the possession. We are deliberately sampling the information that most directly shapes this particular choice.\n\n**Important:** That does not mean omitted information has no effect. Team spacing, score, fatigue, and other defenders could change the outcome later. It means we have made a defensible choice about what to prioritize now, and we will eventually test whether the read survives when other information returns.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Three levels of practice information",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "**Technique information:** Feedback about how a movement was executed, such as how the ball was controlled in the air. It may be available even without a defender.\n\n**Task information:** The time, space, target, or movement requirement that shapes execution, such as a narrow stopping window.\n\n**Game-specifying information:** The actual changes in defenders, teammates, or play that signal when a particular basketball option is available.\n\nThese labels are **BAM teaching shorthand**, not an official scientific three-part taxonomy. The categories can overlap. They help coaches avoid assuming that every reactive drill trains the same kind of reaction.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Case study | Reaction without the game read",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "In The Modern Basketball Blueprint, **Audio-Cued Stop Shots** ask the player to attack at speed and stop for a jumper when they hear “stop.” The player responds to a changing signal and practices deceleration. However, an audio cue does not specify a defender's stance, momentum, or angle.",
          "depth": "cert"
        },
        {
          "kind": "writtenResponse",
          "id": "audiocue",
          "prompt": "**Prompt:** If this activity improves the player's deceleration, what would you need to add or test before claiming that their pull-up selection against defense improved?",
          "discussion": "Look for an actual defensive interaction and a decision window. The athlete might now have greater action capacity, but we still need to know whether they see the opportunity and organize the jumper at the right time. Better stopping ability and better game selection are related possibilities, not interchangeable outcomes.",
          "depth": "cert"
        }
      ]
    },
    {
      "id": "better",
      "title": "Representative Isn't Automatically Better",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "Representative Isn't Automatically Better"
        },
        {
          "kind": "text",
          "md": "Here is the part that can get lost in conversations about ecological dynamics: **a practice task can be valuable without being very representative of the game.**\n\nIn The Modern Basketball Blueprint, BAM discusses players who struggle to manipulate the ball in the air during contested finishes. They might repeatedly avoid that movement solution, even in good live games, because another finishing strategy works well enough.\n\nSo we sometimes step away from game-like finishes and overload the missing puzzle piece. The book uses **full-circle finishes**, **behind-the-back finishes**, and **touch finishes**, in which the athlete touches a body part in mid-air before completing the layup.\n\nThese are deliberately unusual. The goal is not to imitate an actual contested layup. The goal is to expand the athlete's capacity to control and reposition the ball in the air. Then we see whether that capacity becomes usable against real defense.\n\nThat final step matters. **Building an option outside the game does not guarantee the athlete will recognize or use it in the game.**\n\nThe same distinction applies to shooting machines. They can help generate a huge number of shooting repetitions. But they usually remove the defender information, variable timing, and many movement demands that regulate game shots. That is not an argument against machines; it is an argument for knowing what they do and do not train."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "3. The Intentions and Incentives Problem",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "What athletes are trying to do changes what they notice and choose. A coach may create a realistic 2v2 situation, then tell players to experiment with unusual finishes instead of maximizing points in the short term.\n\nThe defender's movement, timing, and spacing can remain game-relevant. But the athlete's decision criterion has changed. They might take an option they would not currently take in a close game. That does not make the exercise meaningless or completely unrepresentative. It means **the objective is now exploration**.\n\nThis is one reason the **Remove / Force / Reprice** framework must be connected to RLD. A new reward can maintain the live game interaction while altering which actions are attractive. That may be exactly what we want if a viable option is underused, but it may also make an unnatural action overvalued.\n\nTreat a decision generated by an artificial scoring system as **practice behavior** first. Then fade or remove the incentive and check whether the athlete selects the action when the game actually affords it.\n\nThe same applies when we remove an option or force a behavior. An athlete can comply perfectly with the constraint without improving their eventual game decision.",
          "depth": "cert"
        }
      ]
    },
    {
      "id": "three",
      "title": "Three Things to Design Together",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "Three Things to Design Together"
        },
        {
          "kind": "text",
          "md": "It is tempting to picture a single slider from high reps at one end to game-like at the other. In reality, they are not opposites.\n\nBAM suggests evaluating three interacting parts of the activity:\n\n**1. Target-specific representativeness:** Are the important demands and information present?\n\n**2. Targeted repetition:** How often does the athlete actually encounter the problem or movement quality we want to develop?\n\n**3. Appropriate challenge:** Is the right thing being challenged at a useful level for this particular athlete today?\n\nAn activity can be strong on all three. It can also be strong on one and weak on the others. More bodies, more chaos, or more repetitions do not automatically make practice better.\n\nFor decision-making, distinguish **total repetitions** from **meaningful exposures to the decision**. Ten possessions are not necessarily ten useful reads. A player might shoot ten times without once having to perceive whether help is coming.\n\nFor a movement target, ask whether the athlete actually trained the missing quality. If an athlete scores the basket by avoiding the movement you wanted to develop, that is a made shot but not necessarily a productive repetition for this goal."
        },
        {
          "kind": "bigIdea",
          "md": "Repetition quality begins with two questions: Are we challenging the right thing? And are we challenging it at the right level?"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "4. The Three-Dimension Design Map",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "When designing practice, evaluate three distinct dimensions: target-specific representativeness, meaningful repetition volume, and appropriate challenge. They interact, but improving one doesn't automatically require sacrificing another.",
          "depth": "cert"
        },
        {
          "kind": "table",
          "head": [
            "Practice configuration",
            "What it may do well",
            "What to watch"
          ],
          "rows": [
            {
              "cells": [
                "**Shooting machine, repeating the same shot**",
                "High ball-release volume, opportunities for technical consistency or touch.",
                "Few or no live defensive reads; challenge may be too low for some athletes."
              ]
            },
            {
              "cells": [
                "**2v2 half-advantage finishing versus dump-off**",
                "Repeated exposures to a specific live help decision; retains perception-action links.",
                "Other game contexts and physical/psychological demands are simplified."
              ]
            },
            {
              "cells": [
                "**Full-circle or touch finishes on-air**",
                "High exposure to an underdeveloped ball-manipulation movement quality.",
                "Movement may not transfer to contest finishing or appropriate selection without reintroduction of game demands."
              ]
            },
            {
              "cells": [
                "**Unstructured 5v5**",
                "Rich interactions, game continuity, physical/psychological and tactical demands.",
                "The target player may rarely face the precise problem being trained."
              ]
            }
          ],
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "**Key idea:** A practice activity may be high in overall representativeness but low in **target-specific repetition**. Another may be low in game resemblance but highly useful for strengthening the missing physical or coordinative capacity. Neither is automatically the better activity.\n\nThis also changes how we count reps:\n\n- **Skill executions:** How many times did the player shoot, pass, jump, or finish?\n- **Target-problem exposures:** How many times did the relevant problem genuinely arise?\n- **High-quality learning opportunities:** Of those exposures, how many appropriately challenged the right thing?\n\nFor decision-making work, the second and third may tell us more than the first. For on-air coordination practice, actual attempts of the target movement quality may matter more. **The useful denominator depends on the learning target.**\n\nThe three dimensions are a **BAM design heuristic**, not a validated equation or standardized scoring instrument. Avoid turning them into arbitrary numerical grades unless those grades are defined and tested.",
          "depth": "cert"
        },
        {
          "kind": "scienceNerd",
          "title": "Fidelity and load are not the same",
          "blocks": [
            {
              "kind": "prose",
              "md": "A 2023 conceptual paper, Many Pieces to the Puzzle: A New Holistic Workload Approach to Designing Practice in Sports, argues that practitioners should consider **representativeness (fidelity)** separately from **physical and cognitive load**. It also notes that research has not yet identified exactly how representative a task must be for transfer, or how to dose highly representative activities across a season. This supports careful workload reasoning; it is not experimental proof of the BAM three-factor framework. [Paper: https://doi.org/10.1186/s40798-023-00575-7]"
            }
          ],
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "5. Evidence From Basketball: What a Defender Changes",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "A particularly relevant 2025 study by Court Gold and colleagues compared shooting tasks **with and without a defender** in skilled female basketball players.\n\nUnder defense, athletes released shots faster, had longer jump times and ball-flight times, showed more within-player variation in aspects of timing, and shot less accurately. Accuracy in the defended condition also correlated strongly with players' competition shooting statistics.\n\n**Why coaches should care:** A defender is not simply decorative. Their presence changed how players organized and timed the shot. Two tasks that both involve a jumper can ask for meaningfully different movement solutions.\n\n**What the study does not prove:** It compared task performance, not months of training. It does **not** show that defended practice necessarily produces greater long-term shooting improvement than undefended practice or that undefended shooting is unnecessary.\n\n[Research card: Court Gold, C. L., et al. (2025), Sampling perception-action couplings from competition create representative basketball shooting tasks: A replication and extension of, Psychology of Sport and Exercise, 78, 102828. https://doi.org/10.1016/j.psychsport.2025.102828]",
          "depth": "cert"
        },
        {
          "kind": "scienceNerd",
          "title": "Don't confuse changing performance with causing learning",
          "blocks": [
            {
              "kind": "prose",
              "md": "The Court Gold et al. study is especially useful because it measured differences in execution under immediate task conditions. It is evidence about **how a constraint changes performance**, and some validity evidence from the competition correlation. It is not a randomized longitudinal test of transfer after different training programs. There is a major difference between **eliciting** a game-like response today and **developing** a response that endures and transfers weeks later."
            }
          ],
          "depth": "cert"
        }
      ]
    },
    {
      "id": "preserve",
      "title": "What to Preserve, What to Remove, What to Exaggerate",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "What to Preserve, What to Remove, What to Exaggerate"
        },
        {
          "kind": "text",
          "md": "A coach cannot preserve every part of competition in every drill, and there is no reason to try.\n\nSome game elements are essential to the learning target. Others create a logistical burden, soak up the athlete's attention, or reduce meaningful repetitions while contributing very little to that specific goal.\n\nSo make deliberate choices:\n\n**Preserve** the information or action demands that define the problem. If the skill is a read on help defense, that live relationship should usually remain.\n\n**Remove or simplify** pieces that consume time or attention without helping the athlete work on the target. A smaller game with fewer players may still preserve the most useful read.\n\n**Exaggerate or isolate** a quality that needs more work than normal play provides. A deliberately unusual finishing or deceleration challenge may give an athlete a reason to explore a solution they avoid in games.\n\nThere is a difference between **reaction** and **game-relevant reaction**, too. The **Audio-Cued Stop Shots** from The Modern Basketball Blueprint require athletes to react to a spoken “stop” and decelerate into a jumper. That is a reactive movement challenge. It does not recreate reading an actual defender's position or speed. Both can be useful. They simply serve different purposes.\n\nOne additional nuance: athletes can receive genuinely useful game information while practicing with a different intention. A player in live 2v2 might intentionally explore an unfamiliar solution instead of selecting only the highest-percentage play. The information can remain representative even though the immediate objective has changed. Whether that exploration eventually helps game decisions still needs to be tested."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "6. Overall Fidelity Still Matters",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Target-specific representativeness helps us make better decisions. It does not mean the rest of basketball is irrelevant.\n\nThe 2v2 half-advantage situation strips away teammates, transitions, fatigue, and a range of tactical possibilities. That is useful when isolating a particular read. But at some point, we need to find out what happens when the athlete is exposed to the broader information of the game.\n\nThis is where **overall representativeness** earns its place. A player might execute the read in one carefully controlled picture but fail to do so when their role changes, another defender helps from a different spot, or the possession transitions rapidly.\n\nYou do not have to add everything at once. A reasonable progression can restore information strategically: more possible help responses, more teammates, less predictable possession starts, then more continuous play. This is an **illustrative design sequence**, not a rigid BAM drill progression.\n\nPsychological and physical demands deserve attention as well. A player who can recognize a read fresh and relaxed might struggle when fatigued or under pressure. Those demands affect performance, but we do not assume that simply adding exhaustion or stressful stakes improves learning. The challenge must still target the thing we care about.\n\nA 2019 tennis study comparing common training tasks with match play found meaningful differences in shot and movement characteristics even when practice appeared sport-specific. That reinforces the danger of judging representativeness by appearance alone. Again, it is assessment evidence, not a universal recipe for optimizing learning. [Study: https://doi.org/10.1016/j.psychsport.2018.11.008]",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "7. Choose What to Remove by Its Cost and Value",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "When building practice, there is a price to every extra constraint, defender, teammate, instruction, and reset.\n\nSome costs are logistical: you do not have enough players or the drill has so many moving pieces that the targeted action appears once every two minutes.\n\nSome costs are attentional: the athlete concentrates on remembering a complicated point system rather than responding to the game.\n\nSome costs are physical: full-speed live play delivers more force and contact than the athlete needs in that session.\n\nDo not remove something just because it is inconvenient. Ask whether it is **critical to the target**. If it is, solve around it if possible. If it is not, simplifying the environment may improve the learning opportunity.\n\n**BAM rule of thumb:** Remove the lowest-value complexity first, not necessarily the most visible part of the game.\n\nThis is a practice-design judgment, not a validated scientific priority ordering. And it is not one-way. If simplifying the task changes the very decision or action we are trying to train, we simplified the wrong thing.",
          "depth": "cert"
        }
      ]
    },
    {
      "id": "back",
      "title": "Bring the Skill Back to the Game",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "Bring the Skill Back to the Game"
        },
        {
          "kind": "text",
          "md": "Suppose we identify a movement quality during live play, train that quality in isolation, and see it improve. Great. We are not finished.\n\nOnce the athlete can access the new organization with enough stability to use it under some variability, we have a reasonable opening to return to more representative situations. We do not need to wait until the movement looks perfect.\n\nIn live practice, the athlete might discover that they still cannot access the movement at game speed, do not perceive when it is useful, or revert to an old solution against pressure. That tells us what to work on next.\n\nA useful BAM cycle is:\n\n**Identify in the game → Isolate or overload → Reintegrate → Evaluate → Adjust.**\n\nThis is not a rigid five-step program. We may move back and forth several times in the same session or across different sessions. The purpose is to prevent isolated improvements from becoming disconnected from actual basketball."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "The Representative Learning Design Check"
        },
        {
          "kind": "text",
          "md": "Before changing the drill, ask:\n\n1. **What exactly are we developing?** A movement, a read, a decision, a physical quality, or something else?\n2. **What game information and demands matter most for that target?**\n3. **What can we remove without losing the target problem?**\n4. **What might we exaggerate to make the limiting quality more accessible to train?**\n5. **Are we getting enough meaningful repetitions at the right difficulty?**\n6. **Where will we test whether the improvement shows up with less artificial support?**\n\nThis is representative practice as an intentional design process, not a contest to see whose drill looks the most like an NBA game."
        },
        {
          "kind": "bigIdea",
          "md": "The goal is not maximum realism in every drill. The goal is a practice environment that develops what the athlete needs and connects it back to basketball."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "8. Reintegrate Early Enough to Find Out What Was Missing",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "One reasonable threshold for returning from isolated movement work to richer game conditions is when an athlete can access the solution **with enough stability to use it amid some variability**. Perfection is not required.\n\nNow we test four different claims:\n\n**Capacity:** Can they execute the developing movement at all?\n\n**Availability:** Can they access it when the conditions vary?\n\n**Selection:** Do they recognize when the option is appropriate during live play?\n\n**Retention and transfer:** Does it recur later, with less coaching and different game situations?\n\nThese are BAM coaching distinctions. They help avoid treating a technically successful drill as proof of game learning.\n\nReturn to your live context. If the option disappears, decide whether the problem is movement capacity, inadequate access under pressure, missing perception, or simply that the game did not afford the option during those repetitions. Do not automatically assume the isolated work failed.\n\nA cycle may look like **live assessment → targeted puzzle-piece training → live constraint-based activity → broader game play → follow-up assessment**. We can revisit the same target across sessions. That is consistent with the play-drill-play philosophy already discussed in BAM's books, without forcing every session into a fixed format.",
          "depth": "cert"
        }
      ]
    },
    {
      "id": "language",
      "title": "A Note on Language",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "A Note on Language"
        },
        {
          "kind": "text",
          "md": "In The Modern Basketball Blueprint, **Representative Practice** is BAM's practical label for live training with defense. We are keeping that usage.\n\n**Representative Learning Design**, in this lesson, is the broader question of how faithfully a task samples the demands relevant to game performance. A live drill can be poorly designed for a particular target. An on-air drill can preserve some game demands and develop a valuable capacity, even though it cannot reproduce live defender information by itself.\n\nThe distinction is about what the activity accomplishes, not assigning every drill a permanent label."
        },
        {
          "kind": "reflection",
          "md": "Think of a drill you use frequently. What is the **one game problem** it is supposed to develop? Which part of that problem is actually present in the activity? And which game-like elements might be missing, unnecessary, or worth exaggerating?\n\nThere is no answer to submit. The next time you run the drill, watch what the athlete really gets to perceive and do.",
          "depth": "platform"
        }
      ]
    },
    {
      "id": "activities",
      "title": "Certification Activities",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "Certification Activities",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "These activities are about making coaching decisions, not memorizing terminology. When an exercise asks you to evaluate an athlete, use someone you've actually coached and describe what you've observed rather than guessing about their abilities.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Activity 1 | Spot the Missing Information",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "**Scenario:** A ball handler repeatedly finishes well in no-defense work but struggles to choose between a finish and a dump-off when a help defender rotates.",
          "depth": "cert"
        },
        {
          "kind": "writtenResponse",
          "id": "missing",
          "prompt": "**Your response:** Identify the target-specific information that must be present to train that choice. Which parts of five-on-five could you remove without deleting the core read? How would you know the defender is behaving realistically enough to create a meaningful decision?",
          "discussion": "**Compare your answer:** The activity needs a help defender who can actually respond and a teammate who can become a passing option. A prearranged pass does not test whether the ball handler perceives and uses the defender's movement. Reducing the number of players is fine if that real decision stays intact. Watch whether the athlete responds to what the help defender does, rather than merely following the drill's instructions.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Activity 2 | Trade-Offs Without a False Spectrum",
          "depth": "cert"
        },
        {
          "kind": "writtenResponse",
          "id": "tradeoffs",
          "prompt": "**Prompt:** Compare (a) full-circle finishes on-air and (b) a 2v2 half-advantage game. Rate each qualitatively as low, medium, or high for target-specific representativeness, useful repetition volume, and appropriate challenge **for two different goals**: mid-air ball manipulation and help-defense selection. Explain why the ratings change with the target.",
          "discussion": "There is no context-free score. Full-circle finishes may expose ball manipulation more repeatedly, but may have little defender-read fidelity. A 2v2 can supply the live read, but an athlete may avoid the desired airborne control. Challenge levels depend on who is training. The same task can be excellent for one aim and poor for another.",
          "depth": "cert"
        }
      ]
    },
    {
      "id": "activity3",
      "title": "Activity 3 | Design Three Versions of One Problem",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "Activity 3 | Design Three Versions of One Problem",
          "depth": "cert"
        },
        {
          "kind": "writtenResponse",
          "id": "threeversions",
          "prompt": "**Prompt:** Choose a specific skill-development problem from an athlete you coach. Design (A) an activity mainly for concentrated target-skill repetitions, (B) a task mainly for preserving the crucial game information, and (C) an activity trying to combine meaningful volume, information, and appropriate challenge. For each, state the target, the information kept, what you deliberately removed, the metric you would observe, and the unintended workaround you would watch for.",
          "discussion": "The strength of the design is not how creative it sounds. It is whether the target is unambiguous, whether each constraint has a job, and whether the coach can see what athletes actually do in response. Sometimes the high-volume and high-fidelity versions belong in different blocks rather than being forced into one overly complex task.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Activity 4 | Apply It to Your Player",
          "depth": "cert"
        },
        {
          "kind": "applyToAthlete",
          "id": "player",
          "title": false,
          "numbered": true,
          "intro": "**Choose an actual player you coach.** Record only what you know or have directly observed.",
          "steps": [
            {
              "id": "situation",
              "q": "Name a game situation where the athlete struggles to use a particular skill, movement, or decision.",
              "hint": "",
              "rows": 3
            },
            {
              "id": "information",
              "q": "Describe the information and demands that shape that situation in real play.",
              "hint": "",
              "rows": 3
            },
            {
              "id": "quality",
              "q": "Identify a quality you might develop more efficiently with fewer game elements present.",
              "hint": "",
              "rows": 3
            },
            {
              "id": "reintroduce",
              "q": "Choose your reintroduction point: what must be stable enough before you test it in a richer setting?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "check",
              "q": "Define a retention/transfer check that you can actually observe later. What finding would make you revise your approach?",
              "hint": "",
              "rows": 3
            }
          ],
          "discussion": "**Compare your answer:** A strong plan explains which game demands you preserved and which you deliberately removed. Ground that decision in what you've observed, and avoid assuming that a drill will transfer before you've tested it. Also distinguish between an athlete who never encountered an opportunity and one who encountered it but didn't recognize or use it. Those are different problems.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Final Reflection",
          "depth": "cert"
        },
        {
          "kind": "writtenResponse",
          "id": "final",
          "prompt": "Before this module, did you tend to judge drills by how much they looked like basketball, or by how efficiently they trained the particular problem you cared about? What will you change?",
          "depth": "cert"
        }
      ]
    },
    {
      "id": "sources",
      "title": "Research and Further Reading",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "Research and Further Reading",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "1. **Pinder, R. A., Davids, K., Renshaw, I., & Araújo, D. (2011).** Representative learning design and functionality of research and practice in sport. Journal of Sport & Exercise Psychology, 33(1), 146–155. **Conceptual framework.** https://doi.org/10.1123/jsep.33.1.146\n2. **Court Gold, C. L., et al. (2025).** Sampling perception-action couplings from competition create representative basketball shooting tasks: A replication and extension of. Psychology of Sport and Exercise, 78, 102828. **Immediate defended versus undefended shooting task comparison; not longitudinal skill-learning evidence.** https://doi.org/10.1016/j.psychsport.2025.102828\n3. **Champion, L., Middleton, K., & MacMahon, C. (2023).** Many Pieces to the Puzzle: A New Holistic Workload Approach to Designing Practice in Sports. Sports Medicine - Open, 9, 38. **Conceptual paper discussing separate fidelity and load.** https://doi.org/10.1186/s40798-023-00575-7\n4. **Krause, L., et al. (2019).** Application of representative learning design for assessment of common practice tasks in tennis. Psychology of Sport and Exercise, 41, 36–45. **Observed task-vs-match differences, not a practice intervention trial.** https://doi.org/10.1016/j.psychsport.2018.11.008\n5. **Coleman Ayers, The Modern Basketball Blueprint.** Chapter 3, Representative Practice, and Chapter 4, Effective On-Air Training (especially printed pp. 86–90, on puzzle-piece finishing and transition-shooting examples).\n6. **Coleman Ayers, Deep Skill.** Chapter 12, The Games Approach (especially printed pp. 157–158).\n\n**What the evidence doesn't settle:** Representative Learning Design provides a useful framework for organizing practice, and research shows that changing practice conditions can change what athletes do immediately. But no universal formula tells us exactly how to balance representativeness, meaningful repetitions, challenge, and physical load for every player. Use these principles to design practice, then evaluate what the athlete actually learns and transfers.",
          "depth": "cert"
        },
        {
          "kind": "relatedConcepts",
          "items": [
            {
              "key": "affordances",
              "title": "Affordances",
              "blurb": "What Does the Player Actually See?"
            },
            {
              "key": "types-of-constraints",
              "title": "Types of Constraints",
              "blurb": "Design the Problem. Let the Athlete Solve It."
            },
            {
              "key": "repetition-without-repetition",
              "title": "Repetition Without Repetition",
              "blurb": "How to Get More Reps Without Just Reciting the Same Solution"
            },
            {
              "key": "challenge-point",
              "title": "Challenge Point & Desirable Difficulties",
              "blurb": "Hard Is Not the Goal. Productive Struggle Is."
            },
            {
              "key": "degrees-of-freedom",
              "title": "Degrees of Freedom",
              "blurb": "Freedom Is Only Useful If You Can Organize It."
            },
            {
              "key": "differential-learning-concept",
              "title": "Differential Learning",
              "blurb": "Push the Borders. Reorganize the Skill."
            },
            {
              "key": "types-of-variability",
              "title": "Types of Variability",
              "blurb": "Stable enough to perform. Adaptable enough to solve."
            },
            {
              "key": "attractors-metastability",
              "title": "Attractors, Multistability & Metastability",
              "blurb": "Stable Enough to Trust. Flexible Enough to Adapt."
            }
          ]
        }
      ]
    }
  ]
};
